// /sends/:id — single send detail. Header with aggregate metrics,
// then a paged recipient grid with per-row open/click/status.

import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, ChevronLeft, ChevronRight } from 'lucide-react';
import { newsletterApi } from '../lib/api';
import type { NewsletterRecipient, NewsletterSend } from '@shared/types';

const PAGE_SIZE = 100;

export default function SendDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [send, setSend] = useState<NewsletterSend | null>(null);
  const [recipients, setRecipients] = useState<NewsletterRecipient[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setErr(null);
    try {
      const res = await newsletterApi.getSend(id, { page, page_size: PAGE_SIZE });
      setSend(res.send);
      setRecipients(res.recipients);
      setTotal(res.total_recipients);
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, [id, page]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  if (loading && !send) {
    return (
      <div className="h-full flex items-center justify-center text-slate-400">Loading…</div>
    );
  }
  if (err || !send) {
    return (
      <div className="h-full flex flex-col items-center justify-center text-slate-500 text-sm gap-2">
        <p>Could not load this send.</p>
        {err && <p className="text-red-600 text-xs">{err}</p>}
        <Link to="/sends" className="text-blue-700 hover:underline">
          Back to sends
        </Link>
      </div>
    );
  }

  return (
    <div className="h-full overflow-y-auto bg-slate-50">
      <div className="max-w-5xl mx-auto py-6 px-4 sm:py-10 sm:px-6">
        <Link
          to="/sends"
          className="text-sm text-slate-500 hover:text-slate-900 flex items-center gap-1 mb-4"
        >
          <ArrowLeft size={14} /> All sends
        </Link>

        <header className="mb-6">
          <h1 className="font-display text-2xl sm:text-3xl text-slate-900 break-words">{send.subject}</h1>
          <p className="text-sm text-slate-500 mt-1">
            {send.sent_at
              ? `Sent ${new Date(send.sent_at).toLocaleString()}`
              : `Created ${new Date(send.created_at).toLocaleString()}`}
            {send.sent_by_email && ` by ${send.sent_by_email}`}
            {' · '}status <strong>{send.status}</strong>
          </p>
        </header>

        <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mb-6">
          <Stat label="Recipients" value={send.recipient_count} />
          <Stat label="Delivered" value={send.delivered_count} total={send.recipient_count} />
          <Stat label="Opened" value={send.opened_count} total={send.recipient_count} />
          <Stat label="Clicked" value={send.clicked_count} total={send.recipient_count} />
          <Stat label="Bounced" value={send.bounced_count} total={send.recipient_count} />
          <Stat label="Spam" value={send.complained_count} total={send.recipient_count} />
        </div>

        {/* Mobile cards */}
        <ul className="sm:hidden bg-white border border-slate-200 rounded-md divide-y divide-slate-100">
          {recipients.map((r) => {
            const fullName = [r.first_name ?? '', r.last_name ?? ''].join(' ').trim();
            const lastEvent = Math.max(
              r.last_clicked_at ?? 0,
              r.last_opened_at ?? 0,
              r.delivered_at ?? 0,
              r.bounced_at ?? 0,
              r.failed_at ?? 0,
            );
            return (
              <li key={r.id}>
                <Link to={`/subscribers/${r.subscriber_id}`} className="block px-4 py-3 active:bg-slate-50">
                  <div className="flex items-baseline justify-between gap-2">
                    <span className="text-sm font-medium text-slate-900 truncate">
                      {fullName || r.email}
                    </span>
                    <span className="text-[11px] text-slate-500 flex-shrink-0">{r.status}</span>
                  </div>
                  {fullName && (
                    <div className="text-xs text-slate-500 truncate mt-0.5">{r.email}</div>
                  )}
                  <div className="text-[11px] text-slate-500 mt-1 flex items-center gap-2 flex-wrap">
                    <span>Opens {r.open_count}</span>
                    <span>·</span>
                    <span>Clicks {r.click_count}</span>
                    {lastEvent > 0 && (
                      <>
                        <span>·</span>
                        <span>{new Date(lastEvent).toLocaleDateString()}</span>
                      </>
                    )}
                  </div>
                </Link>
              </li>
            );
          })}
        </ul>

        {/* Desktop table */}
        <div className="hidden sm:block bg-white border border-slate-200 rounded-md overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
              <tr>
                <th className="text-left px-4 py-2 font-medium">Subscriber</th>
                <th className="text-left px-4 py-2 font-medium">Status</th>
                <th className="text-right px-4 py-2 font-medium">Opens</th>
                <th className="text-right px-4 py-2 font-medium">Clicks</th>
                <th className="text-left px-4 py-2 font-medium">Last activity</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {recipients.map((r) => {
                const fullName = [r.first_name ?? '', r.last_name ?? ''].join(' ').trim();
                const lastEvent = Math.max(
                  r.last_clicked_at ?? 0,
                  r.last_opened_at ?? 0,
                  r.delivered_at ?? 0,
                  r.bounced_at ?? 0,
                  r.failed_at ?? 0,
                );
                return (
                  <tr key={r.id} className="hover:bg-slate-50">
                    <td className="px-4 py-2 text-slate-900">
                      <Link
                        to={`/subscribers/${r.subscriber_id}`}
                        className="hover:underline"
                      >
                        {fullName ? `${fullName} <${r.email}>` : r.email}
                      </Link>
                    </td>
                    <td className="px-4 py-2 text-slate-700">{r.status}</td>
                    <td className="px-4 py-2 text-right text-slate-900">{r.open_count}</td>
                    <td className="px-4 py-2 text-right text-slate-900">{r.click_count}</td>
                    <td className="px-4 py-2 text-slate-500 text-xs">
                      {lastEvent > 0 ? new Date(lastEvent).toLocaleString() : '—'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div className="flex items-center justify-end gap-2 mt-4 text-xs text-slate-600">
          <span>Page {page} of {totalPages}</span>
          <button
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page <= 1 || loading}
            className="btn-ghost p-2 sm:p-1 disabled:opacity-40"
          >
            <ChevronLeft size={16} />
          </button>
          <button
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page >= totalPages || loading}
            className="btn-ghost p-2 sm:p-1 disabled:opacity-40"
          >
            <ChevronRight size={16} />
          </button>
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value, total }: { label: string; value: number; total?: number }) {
  return (
    <div className="bg-white border border-slate-200 rounded-md p-3">
      <div className="text-xs uppercase tracking-wide text-slate-500">{label}</div>
      <div className="text-2xl font-display text-slate-900">{value}</div>
      {total !== undefined && total > 0 && (
        <div className="text-xs text-slate-500">{Math.round((value / total) * 100)}%</div>
      )}
    </div>
  );
}
